import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { CloudUpload, HelpOutline } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import { apiService } from '../services/api';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import type { TaskResponse } from '../types/timekeeping';

const POLL_INTERVAL_MS = 4000;
const TASK_TIMEOUT_MS = 15 * 60 * 1000;
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'WARNING']);

function needsPolling(t: TaskResponse): boolean {
  if (TERMINAL_STATUSES.has(t.status)) return false;
  return Date.now() - new Date(t.createdAt).getTime() < TASK_TIMEOUT_MS;
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'WARNING') return 'warning';
  if (status === 'PROCESSING' || status === 'PENDING') return 'info';
  return 'default';
}

export default function MaintenanceImport() {
  const dispatch = useAppDispatch();

  // Configuration
  const [s3Uri, setS3Uri] = useState<string>('');
  const [configLoading, setConfigLoading] = useState<boolean>(true);
  const [configError, setConfigError] = useState<string | null>(null);

  // Import Task State
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Employee Tags Import State
  const [tagsFile, setTagsFile] = useState<File | null>(null);
  const [tagsImporting, setTagsImporting] = useState<boolean>(false);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [tagsSuccess, setTagsSuccess] = useState<string | null>(null);
  const tagsInputRef = useRef<HTMLInputElement | null>(null);

  // Employee ZIP Import State
  const [employeeFile, setEmployeeFile] = useState<File | null>(null);
  const employeeInputRef = useRef<HTMLInputElement | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch target S3 config
  const fetchConfig = useCallback(async () => {
    try {
      setConfigError(null);
      const res = await apiService.getEmployeeImportConfig();
      setS3Uri(res.data.s3Uri);
    } catch (err: any) {
      setConfigError(err?.message ?? 'Failed to load import configurations.');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Fetch tasks in EMPLOYEE_IMPORT group
  const fetchImportTasks = useCallback(async () => {
    try {
      const res = await apiService.listTasks('EMPLOYEE_IMPORT');
      const updated = res.data ?? [];
      setTasks(updated);
      return updated;
    } catch (err: any) {
      slogError(err);
      return [];
    } finally {
      setTasksLoading(false);
    }
  }, []);

  // Start polling active tasks
  const startPolling = useCallback((currentTasks: TaskResponse[]) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!currentTasks.some(needsPolling)) return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await apiService.listTasks('EMPLOYEE_IMPORT');
        const updated = res.data ?? [];
        setTasks(updated);
        if (!updated.some(needsPolling)) {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        // Retry next cycle
      }
    }, POLL_INTERVAL_MS);
  }, []);

  // Triggered on page mount
  useEffect(() => {
    fetchConfig();
    fetchImportTasks().then(startPolling);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchConfig, fetchImportTasks, startPolling]);

  // Handle employee ZIP file selection
  const handleEmployeeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEmployeeFile(file);
      setError(null);
    }
  };

  // Start employee import (upload ZIP then trigger import)
  const handleStartImport = async () => {
    if (!employeeFile) {
      setError("Please select an employee ZIP file first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const key = 'import-employees.zip';

      // 1. Get pre-signed upload URL
      const uploadRes = await apiService.getUploadUrl(key, employeeFile.type || 'application/zip');
      
      // 2. Upload file to S3
      await fetch(uploadRes.data.url, {
         method: 'PUT',
         body: employeeFile,
         headers: { 'Content-Type': employeeFile.type || 'application/zip' },
      });

      // 3. Trigger import task on backend
      await apiService.startEmployeeImport();
      
      dispatch(
        showNotification({
          message: 'Employee import task scheduled successfully.',
          severity: 'success',
        })
      );

      setEmployeeFile(null);
      if (employeeInputRef.current) {
        employeeInputRef.current.value = '';
      }

      const updated = await fetchImportTasks();
      startPolling(updated);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to trigger import task.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle employee tags file selection
  const handleTagsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTagsFile(file);
      setTagsError(null);
      setTagsSuccess(null);
    }
  };

  // Upload and import employee tags
  const handleTagsUpload = async () => {
    if (!tagsFile) return;

    setTagsImporting(true);
    setTagsError(null);
    setTagsSuccess(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const json = JSON.parse(text);
        if (!json.data || !Array.isArray(json.data)) {
          throw new Error("Invalid file format. The JSON file must contain a 'data' array.");
        }
        const res = await apiService.importEmployeeTags({ data: json.data });
        setTagsSuccess(`Employee tags imported successfully! Added ${res.data.imported} new tags, matched ${res.data.matched} existing tags.`);
        dispatch(
          showNotification({
            message: 'Employee tags imported successfully.',
            severity: 'success',
          })
        );
        setTagsFile(null);
        if (tagsInputRef.current) {
          tagsInputRef.current.value = '';
        }
      } catch (err: any) {
        setTagsError(err?.message ?? 'Failed to import employee tags.');
      } finally {
        setTagsImporting(false);
      }
    };
    reader.onerror = () => {
      setTagsError('Failed to read the selected file.');
      setTagsImporting(false);
    };
    reader.readAsText(tagsFile);
  };

  const isImportRunning = tasks.some(needsPolling);
  const activeTask = tasks.find(needsPolling);

  return (
    <DashboardLayout>
      <Box sx={{ p: 3, maxWidth: 1000 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Import Manager
        </Typography>

        {configError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {configError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Employee Import Card */}
          <Grid size={{ xs: 12 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Import Employees
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
                  Executes employee syncing asynchronously from a ZIP archive of JSON records uploaded to S3. 
                  Matches existences on both extracted UUID and internal IDs, resolving and merging details.
                </Typography>

                <Alert severity="info" icon={<HelpOutline />} sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Import Requirements:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Select a single <span style={{ fontFamily: 'monospace' }}>.zip</span> archive containing one or more employee <span style={{ fontFamily: 'monospace' }}>.json</span> files.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Target S3 Path: {configLoading ? 'Loading...' : s3Uri}
                  </Typography>
                </Alert>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={submitting || isImportRunning}
                  >
                    Select Employees ZIP File
                    <input
                      type="file"
                      hidden
                      accept=".zip"
                      onChange={handleEmployeeFileChange}
                      ref={employeeInputRef}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {employeeFile ? employeeFile.name : 'No file selected'}
                  </Typography>
                </Stack>

                {activeTask && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          Running Task Progress ({activeTask.progressPercent}%)
                        </Typography>
                        <Chip size="small" label={activeTask.status} color="info" />
                      </Stack>
                      <LinearProgress variant="determinate" value={activeTask.progressPercent} sx={{ height: 6, borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        Log: {activeTask.logs?.at(-1) ?? 'Initializing importer...'}
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                  onClick={handleStartImport}
                  disabled={submitting || isImportRunning || configLoading || !employeeFile}
                  fullWidth
                >
                  {isImportRunning ? 'Import in Progress' : 'Upload and Start Import'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Employee Tags Import Card */}
          <Grid size={{ xs: 12 }}>
            <Card variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Import Employee Tags
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2.5 }}>
                  Synchronously imports and matches employee tag structures from an uploaded JSON file.
                  Existing records will be checked for strict matches, and any new tags will be safely written while preserving IDs and DNA pathways.
                </Typography>

                <Alert severity="info" icon={<HelpOutline />} sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Requirements:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Select a single <span style={{ fontFamily: 'monospace' }}>EmployeeTags.json</span> file. 
                    The file should follow the layout containing a root <span style={{ fontFamily: 'monospace' }}>"data"</span> array of tags.
                  </Typography>
                </Alert>

                {tagsError && (
                  <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setTagsError(null)}>
                    {tagsError}
                  </Alert>
                )}

                {tagsSuccess && (
                  <Alert severity="success" sx={{ mb: 2.5 }} onClose={() => setTagsSuccess(null)}>
                    {tagsSuccess}
                  </Alert>
                )}

                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={tagsImporting}
                  >
                    Select Tags File
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={handleTagsFileChange}
                      ref={tagsInputRef}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {tagsFile ? tagsFile.name : 'No file selected'}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  disabled={!tagsFile || tagsImporting}
                  onClick={handleTagsUpload}
                  startIcon={tagsImporting ? <CircularProgress size={18} color="inherit" /> : null}
                  fullWidth
                >
                  {tagsImporting ? 'Importing...' : 'Upload and Import'}
                </Button>
              </CardContent>
            </Card>
          </Grid>


          {/* Task History Table */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1.5 }}>
              Import Tasks History
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Task ID / Label</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Records (Parsed/Written/Failed)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status Message</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Started At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasksLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Loading task history...
                      </TableCell>
                    </TableRow>
                  ) : tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No import tasks found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((t) => {
                      const out = t.outputData as any;
                      const lastLog = t.logs?.at(-1) ?? '';
                      return (
                        <TableRow key={t.taskId} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {t.label || t.taskId.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <Chip label={t.status} color={statusColor(t.status)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {out
                              ? `${out.parsed ?? '0'} / ${out.written ?? '0'} / ${out.failed ?? '0'}`
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word', color: 'text.secondary', fontSize: '0.8rem' }}>
                            {lastLog}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                            {new Date(t.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

    </DashboardLayout>
  );
}

function slogError(err: any) {
  console.error("Task list retrieval failed:", err);
}
